import { Box, Flex, Heading, ScrollArea, Text } from '@radix-ui/themes';
import { Accordion } from 'radix-ui';
import { PropsWithChildren } from 'react';
import { FacetControl } from './FacetControl';
import { FacetLauncher } from './FacetLauncher';
import { FilterSummary } from './FilterSummary';
import { SectionWrapper } from './SectionWrapper';
import { StickyToggle } from './StickyToggle';

import { AnyObject } from '@common/types/queryState';
import { Trans } from 'react-i18next';
import { useFacetingContext } from '../context';
import { FacetType } from '../types';
import styles from './FacetingPanel.module.css';

type FacetingPanelRootProps = PropsWithChildren;
interface FacetingPanelHeaderProps {
    title: string;
}

export const FacetingPanel = {
    Root: ({ children }: FacetingPanelRootProps) => (
        <Flex direction='column' height='100%' overflow='hidden'>
            {children}
        </Flex>
    ),

    Header: <TEntity extends AnyObject = AnyObject>({
        title,
    }: FacetingPanelHeaderProps) => {
        const {
            isStickyEnabled,
            setIsStickyEnabled,
            hasBaseline,
            showStickyToggle = false,
            realTimeMatches,
        } = useFacetingContext<TEntity>();

        return (
            <Flex
                className={styles.header}
                direction='column'
                gap='3'
                p='3'
                flexShrink='0'
            >
                <Heading size='4'>{title}</Heading>

                <Text>
                    <Trans
                        // i18nKey={`facetingPanel:${isSticky ? `matches` : `results`}`}
                        i18nKey={'facetingPanel:results'}
                        values={{ totalCount: realTimeMatches }}
                    />
                </Text>

                {showStickyToggle && (
                    <StickyToggle
                        active={isStickyEnabled}
                        onChange={setIsStickyEnabled}
                        hasBaseline={hasBaseline}
                    />
                )}

                <FilterSummary />
            </Flex>
        );
    },

    Content: <TEntity extends AnyObject = AnyObject>() => {
        const { visibleConfig, filterState, getSectionLabel, onUnpin } =
            useFacetingContext<TEntity>();

        const { selectedFacets, selectedRanges, clearField } = filterState;
        const checkIsActive = (key: string, type: FacetType) => {
            if (type === 'boolean') {
                return (selectedFacets[key]?.length ?? 0) > 0;
            }
            const range = selectedRanges[key];
            return !!(range?.min !== undefined || range?.max !== undefined);
        };

        return (
            <ScrollArea type='scroll'>
                <Accordion.Root type='multiple' asChild>
                    <Flex direction='column' height={'100%'}>
                        {visibleConfig.map(({ key, type, canUnpin }) => (
                            <SectionWrapper
                                key={key}
                                facetKey={key}
                                label={getSectionLabel(key)}
                                hasActiveFilter={checkIsActive(key, type)}
                                canUnpin={!!canUnpin}
                                onClear={() => clearField(key)}
                                onUnpin={() => onUnpin(key)}
                            >
                                <FacetControl facetKey={key} type={type} />
                            </SectionWrapper>
                        ))}
                    </Flex>
                </Accordion.Root>
            </ScrollArea>
        );
    },

    Footer: () => (
        <Box className={styles.footer} p='3' flexShrink='0'>
            <FacetLauncher />
        </Box>
    ),
};
